import { useEffect } from 'react';
import { setCustomAttribute, NR_ATTR_RUNTIME_CONTEXT, NR_ATTR_VIEW, NR_ATTR_EVENT_HANDLE, NR_ATTR_AUTH_STATE, NR_ATTR_SSR_REQUEST_ID } from '@shared/utils/logger';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useAuth } from '@shared/components/AuthProvider';
import queryString from '@shared/core/queryString';
import { ALLOWLIST_CUSTOM_PAGE_SLUGS } from '../GuestSite.constants';

const RUNTIME_CONTEXT_GUEST = 'guest';

const KNOWN_PAGE_TYPES: ReadonlySet<string> = new Set(['welcome', 'story', 'faq', 'schedule', 'travel', 'registry', 'rsvp', 'vip', 'moments']);

const KNOWN_SLUG_TO_VIEW: Readonly<Record<string, string>> = {
  registrylinks: 'registry',
  weddingparty: 'vip',
  accommodations: 'accommodations',
};

// Use the explicit ?preview=true query param rather than isInIframe() — iframes can appear
// in other legitimate contexts (e.g. masked/custom domains) that are not preview sessions.
// The server skips SSR for all ?preview=true requests (server.ts), so this is the
// canonical signal for admin preview context. Stable for the entire session.
const IS_PREVIEWING = queryString.getValueString('preview') === 'true';

function deriveView(pageSlug: string | undefined, isCustomPageSlug: boolean): string {
  // Root URL (/:eventHandle) has no pageSlug. The actual view shown depends on
  // the event's default page (first enabled page), which is not known at this
  // level without GQL data. Use 'root' rather than hardcoding 'welcome' since
  // events whose default page is schedule, custom, etc. would be mislabelled.
  if (!pageSlug) return 'root';
  const slug = pageSlug.toLowerCase();
  if (KNOWN_PAGE_TYPES.has(slug)) return slug;
  if (KNOWN_SLUG_TO_VIEW[slug]) return KNOWN_SLUG_TO_VIEW[slug];
  if (isCustomPageSlug || ALLOWLIST_CUSTOM_PAGE_SLUGS.has(slug)) return 'custom';
  return 'other';
}

/**
 * Derive the auth state label for New Relic.
 *
 * Values:
 * - `'authenticated'` — user has a valid, settled session (logged in)
 * - `'anonymous'`     — auth bootstrap completed successfully, no session
 * - `'pending'`       — auth hasn't resolved yet (first paint before token check)
 * - `'error'`         — auth bootstrap failed (network error, getMe failure, etc.)
 *
 * Uses `isCurrentUserSettled` rather than just `hasInitializedOnce` to distinguish
 * genuine anonymous visitors from auth failures — `hasInitializedOnce` is set to
 * true even when getMe errors out (requestStatus = 'failure'), which would falsely
 * classify failed auth as anonymous if we only checked `isLoggedIn`.
 *
 * All four values are emitted as NR attributes (including `pending`) so that every
 * PageViewTiming event carries `joy.authState` — the first LCP event fires before
 * auth resolves, so omitting pending would leave it without the attribute entirely.
 * Dashboard queries filter to `IN ('authenticated', 'anonymous')` for clean metrics.
 */
function deriveAuthState(hasInitializedOnce: boolean, isCurrentUserSettled: boolean, isLoggedIn: boolean): 'authenticated' | 'anonymous' | 'pending' | 'error' {
  if (!hasInitializedOnce) return 'pending';
  if (!isCurrentUserSettled) return 'error';
  return isLoggedIn ? 'authenticated' : 'anonymous';
}

/**
 * Sets New Relic Browser custom attributes for guest site page views.
 *
 * Sets `joy.runtimeContext = 'guest'` and `joy.view = <page name>` on every
 * PageViewTiming event so LCP dashboards can reliably filter and triage
 * guest site performance separate from admin, marketing, and shop pages.
 *
 * Also sets `joy.authState` to track cookie-auth adoption and segment perf
 * by auth state without exposing any token or session data. The attribute is
 * set immediately as 'pending' and updated once auth bootstrap resolves, so
 * every PageViewTiming event (including the initial LCP) carries the attribute.
 *
 * Skipped entirely when the page is loaded with `?preview=true` (admin website
 * editor preview) — those are not real guest visits and should not appear in LCP dashboards.
 *
 * See: doc/improve-guest-site-perf/07-instrument-site-perf-tracking/README.md
 */
export function useNRGuestAttributes(eventHandle: string): void {
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);
  const view = deriveView(currentPageSlug, isCustomPageSlug);
  const { isLoggedIn, hasInitializedOnce, isCurrentUserSettled } = useAuth();
  const authState = deriveAuthState(hasInitializedOnce, isCurrentUserSettled, isLoggedIn);

  // Set/update attributes on mount and whenever the derived view changes.
  useEffect(() => {
    if (IS_PREVIEWING) return;
    setCustomAttribute(NR_ATTR_RUNTIME_CONTEXT, RUNTIME_CONTEXT_GUEST);
    setCustomAttribute(NR_ATTR_VIEW, view);
    setCustomAttribute(NR_ATTR_EVENT_HANDLE, eventHandle);
  }, [view, eventHandle]);

  // Set auth state on every change — including the initial 'pending' value so the
  // attribute is present on the first PageViewTiming/LCP event. Updated to
  // 'authenticated' or 'anonymous' once auth settles.
  useEffect(() => {
    if (IS_PREVIEWING) return;
    setCustomAttribute(NR_ATTR_AUTH_STATE, authState);
  }, [authState]);

  // Set SSR request ID once on mount — correlates client-side PageViewTiming (LCP, FCP)
  // with the server-side SSR transaction in NR for end-to-end request tracing.
  // Only set when the page was server-rendered (window.__SSR_REQUEST_ID__ injected by SSR).
  useEffect(() => {
    if (IS_PREVIEWING) return;
    const ssrRequestId = window.__SSR_REQUEST_ID__;
    if (ssrRequestId) {
      setCustomAttribute(NR_ATTR_SSR_REQUEST_ID, ssrRequestId);
    }
  }, []);

  // Clear on unmount (GuestSite leaving the tree entirely, e.g. SPA nav to /account).
  // Prevents stale 'guest' values from persisting on non-guest page events.
  // NR has no delete API; empty string is the neutral sentinel.
  useEffect(() => {
    if (IS_PREVIEWING) return;
    return () => {
      setCustomAttribute(NR_ATTR_RUNTIME_CONTEXT, '');
      setCustomAttribute(NR_ATTR_VIEW, '');
      setCustomAttribute(NR_ATTR_EVENT_HANDLE, '');
      setCustomAttribute(NR_ATTR_AUTH_STATE, '');
      setCustomAttribute(NR_ATTR_SSR_REQUEST_ID, '');
    };
  }, []);
}
